/**
 * ProductionOnboarding Component
 * Modern, production-ready onboarding flow for first-time users
 * P1-5: First-Time User Onboarding Implementation
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Check, Sparkles, BookOpen, DollarSign, Heart, Target, Rocket } from 'lucide-react';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { Button } from '../ui/button';
import { supabase } from '../../lib/supabase';

interface OnboardingStep {
  id: string;
  title: string;
  subtitle: string;
  content: React.ReactNode;
  canSkip?: boolean;
}

interface OnboardingPreferences {
  focusArea?: 'LIFE' | 'MONEY' | 'JOY' | 'balanced';
  pace?: 'self-paced' | 'guided';
  goals?: string[];
  hasCompletedOnboarding?: boolean;
}

export const ProductionOnboarding: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [preferences, setPreferences] = useState<OnboardingPreferences>({});

  const steps: OnboardingStep[] = [
    {
      id: 'welcome',
      title: 'Welcome to Hover',
      subtitle: 'Your journey to Agency + Kindness begins here',
      content: <WelcomeStep />,
      canSkip: false,
    },
    {
      id: 'modules',
      title: 'Three Core Modules',
      subtitle: 'LIFE, MONEY, and JOY work together',
      content: <ModulesStep />,
      canSkip: false,
    },
    {
      id: 'focus',
      title: 'Choose Your Starting Point',
      subtitle: 'Where would you like to begin?',
      content: (
        <FocusStep
          selectedFocus={preferences.focusArea}
          onSelectFocus={(focus) => setPreferences({ ...preferences, focusArea: focus })}
        />
      ),
      canSkip: false,
    },
    {
      id: 'ready',
      title: "You're Ready to Begin",
      subtitle: 'Your transformation journey starts now',
      content: <ReadyStep preferences={preferences} />,
      canSkip: false,
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeOnboarding = async () => {
    setIsLoading(true);

    try {
      // Save preferences to profile
      if (user) {
        const { error } = await supabase
          .from('profiles')
          .update({
            onboarding_completed: true,
            focus_area: preferences.focusArea || 'balanced',
            updated_at: new Date().toISOString(),
          })
          .eq('id', user.id);

        if (error) {
          console.error('Error saving onboarding preferences:', error);
        }
      }

      // Mark as complete locally
      localStorage.setItem('onboarding_completed', 'true');
      localStorage.setItem('onboarding_focus', preferences.focusArea || 'balanced');

      setIsComplete(true);

      // Wait for celebration animation, then navigate
      setTimeout(() => {
        // Call onComplete callback
        onComplete();

        // Force navigation as backup (in case callback doesn't work)
        setTimeout(() => {
          const focusArea = preferences.focusArea || 'balanced';
          if (focusArea === 'LIFE') {
            window.location.href = '/life';
          } else if (focusArea === 'MONEY') {
            window.location.href = '/money';
          } else if (focusArea === 'JOY') {
            window.location.href = '/joy';
          } else {
            window.location.href = '/';
          }
        }, 500); // Extra 500ms backup
      }, 2500);
    } catch (error) {
      console.error('Error completing onboarding:', error);
      setIsLoading(false);
    }
  };

  const skipOnboarding = () => {
    localStorage.setItem('onboarding_completed', 'true');
    onComplete();
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  if (isComplete) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-gradient-to-br from-purple-900 via-black to-pink-900 flex items-center justify-center z-50"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 100, delay: 0.2 }}
          className="text-center"
        >
          <motion.div
            animate={{
              rotate: [0, 360],
            }}
            transition={{ duration: 1, ease: "easeInOut" }}
            className="mb-6"
          >
            <div className="w-24 h-24 mx-auto bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
              <Check className="w-12 h-12 text-white" />
            </div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-3xl font-bold text-white mb-2"
          >
            You're All Set!
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-gray-300"
          >
            Your journey begins now... 🚁
          </motion.p>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-gradient-to-br from-purple-900 via-black to-pink-900 flex items-center justify-center z-50 overflow-y-auto"
    >
      <div className="max-w-3xl w-full mx-auto p-8 my-8">
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            <span className="text-sm text-gray-400">
              Step {currentStep + 1} of {steps.length}
            </span>
            {steps[currentStep].canSkip && (
              <button
                onClick={skipOnboarding}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                Skip for now
              </button>
            )}
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
            />
          </div>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/10"
          >
            <div className="mb-8 text-center">
              <h2 className="text-3xl font-bold text-white mb-2">
                {steps[currentStep].title}
              </h2>
              <p className="text-lg text-gray-300">
                {steps[currentStep].subtitle}
              </p>
            </div>

            <div className="mb-8">
              {steps[currentStep].content}
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Button
                onClick={handlePrevious}
                disabled={currentStep === 0}
                variant="ghost"
                className="text-white/60 hover:text-white disabled:opacity-30"
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Previous
              </Button>

              <Button
                onClick={handleNext}
                disabled={isLoading || (currentStep === 2 && !preferences.focusArea)}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
              >
                {isLoading ? (
                  'Saving...'
                ) : currentStep === steps.length - 1 ? (
                  <>
                    Begin Journey
                    <Rocket className="w-5 h-5 ml-2" />
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

/**
 * Step Components
 */

const WelcomeStep: React.FC = () => (
  <div className="text-center space-y-6">
    <div className="inline-block">
      <motion.div
        animate={{
          scale: [1, 1.1, 1],
          rotate: [0, 5, -5, 0],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <Sparkles className="w-20 h-20 text-yellow-400 mx-auto" />
      </motion.div>
    </div>

    <div className="max-w-xl mx-auto space-y-4 text-gray-300">
      <p className="text-lg">
        You're about to embark on a journey to discover your power to create positive change through{' '}
        <strong className="text-white">Agency + Kindness</strong>.
      </p>

      <p>
        This isn't a course or a program—it's a <em className="text-purple-300">personalized transformation experience</em> powered by AI coaching.
      </p>

      <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4 mt-6">
        <p className="text-sm text-purple-200">
          <strong>What makes Hover different:</strong> We focus on depth over content. Instead of consuming endless lessons, you'll explore your own stories, decisions, and connections to unlock lasting change.
        </p>
      </div>
    </div>
  </div>
);

const ModulesStep: React.FC = () => (
  <div className="space-y-6">
    <div className="grid md:grid-cols-3 gap-6">
      {[
        {
          icon: BookOpen,
          name: 'LIFE',
          color: 'from-red-500 to-orange-500',
          description: 'Capture stories that reveal your values and agency',
        },
        {
          icon: DollarSign,
          name: 'MONEY',
          color: 'from-green-500 to-emerald-500',
          description: 'Make decisions that create wealth and wellbeing',
        },
        {
          icon: Heart,
          name: 'JOY',
          color: 'from-yellow-500 to-amber-500',
          description: 'Build a network of meaningful connections',
        },
      ].map((module, index) => (
        <motion.div
          key={module.name}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center hover:border-white/20 transition-all"
        >
          <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-br ${module.color} rounded-2xl flex items-center justify-center`}>
            <module.icon className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">{module.name}</h3>
          <p className="text-sm text-gray-300">{module.description}</p>
        </motion.div>
      ))}
    </div>

    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
      <p className="text-sm text-gray-300 text-center">
        All three modules work together. Data from one module enriches the others, creating a{' '}
        <strong className="text-white">holistic view of your transformation</strong>.
      </p>
    </div>
  </div>
);

interface FocusStepProps {
  selectedFocus?: string;
  onSelectFocus: (focus: 'LIFE' | 'MONEY' | 'JOY' | 'balanced') => void;
}

const FocusStep: React.FC<FocusStepProps> = ({ selectedFocus, onSelectFocus }) => {
  const options = [
    {
      id: 'LIFE' as const,
      icon: BookOpen,
      title: 'LIFE Stories',
      description: 'I want to understand my values and agency patterns first',
    },
    {
      id: 'MONEY' as const,
      icon: DollarSign,
      title: 'MONEY Decisions',
      description: 'I have financial decisions to explore right now',
    },
    {
      id: 'JOY' as const,
      icon: Heart,
      title: 'JOY Connections',
      description: 'I want to map and strengthen my relationships',
    },
    {
      id: 'balanced' as const,
      icon: Target,
      title: 'Balanced Approach',
      description: 'I want to explore all three areas equally',
    },
  ];

  return (
    <div className="space-y-4">
      {options.map((option) => (
        <button
          key={option.id}
          onClick={() => onSelectFocus(option.id)}
          className={`w-full text-left p-6 rounded-xl border transition-all ${
            selectedFocus === option.id
              ? 'bg-purple-500/20 border-purple-500/50 ring-2 ring-purple-500/50'
              : 'bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/10'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
              selectedFocus === option.id ? 'bg-purple-500/30' : 'bg-white/10'
            }`}>
              <option.icon className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-white mb-1">{option.title}</h4>
              <p className="text-sm text-gray-300">{option.description}</p>
            </div>
            {selectedFocus === option.id && (
              <Check className="w-5 h-5 text-purple-400 flex-shrink-0" />
            )}
          </div>
        </button>
      ))}

      <p className="text-xs text-gray-400 text-center mt-4">
        Don't worry—you can explore all modules at any time. This just helps us personalize your experience.
      </p>
    </div>
  );
};

const ReadyStep: React.FC<{ preferences: OnboardingPreferences }> = ({ preferences }) => (
  <div className="text-center space-y-6">
    <div className="inline-block">
      <motion.div
        animate={{
          y: [0, -10, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <Rocket className="w-20 h-20 text-purple-400 mx-auto" />
      </motion.div>
    </div>

    <div className="max-w-xl mx-auto space-y-4">
      <p className="text-lg text-gray-300">
        You're all set! Here's what happens next:
      </p>

      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-left space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-sm">1</span>
          </div>
          <p className="text-sm text-gray-300">
            You'll land in the{' '}
            <strong className="text-white">
              {preferences.focusArea === 'balanced' ? 'Home Portal' : `${preferences.focusArea} module`}
            </strong>
          </p>
        </div>

        <div className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-sm">2</span>
          </div>
          <p className="text-sm text-gray-300">
            <strong className="text-white">AI Coach</strong> is available when you need help (but never intrusive)
          </p>
        </div>

        <div className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-sm">3</span>
          </div>
          <p className="text-sm text-gray-300">
            Everything auto-saves, so you can leave and come back anytime
          </p>
        </div>

        <div className="flex items-start gap-3">
          <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-sm">4</span>
          </div>
          <p className="text-sm text-gray-300">
            Your data is private and only visible to you
          </p>
        </div>
      </div>

      <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4 mt-6">
        <p className="text-sm text-purple-200">
          <strong>Remember:</strong> This is your journey. Go at your own pace, explore freely, and trust the process. 🚁
        </p>
      </div>
    </div>
  </div>
);

export default ProductionOnboarding;
